/**
 * Vector Store — IndexedDB-backed storage for entry embeddings.
 * DB: `konstantout-vectors`, stores: `embeddings` + `meta`.
 * Schema versions: v1 arctic-embed 384-dim, v2 EmbeddingGemma 768-dim
 */

// --- Types -------------------------------------------------------------------

export interface EmbeddingRecord {
  entryId: string
  embedding: number[]
  contentHash: string
  updatedAt: number
}

export interface SimilarityResult {
  entryId: string
  score: number
}

// --- Constants ---------------------------------------------------------------

/** Current embedding schema version. Bump when model/dim/prefix changes. */
export const SCHEMA_VERSION = 8

const DB_NAME = 'konstantout-vectors'
const STORE_NAME = 'embeddings'
const META_STORE = 'meta'
const DB_VERSION = 2

// --- Database ----------------------------------------------------------------

export function openVectorDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'entryId' })
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE)
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

// --- Schema version ----------------------------------------------------------

/** Read the stored schema version. Returns 0 if not yet set. */
export async function getSchemaVersion(): Promise<number> {
  const db = await openVectorDB()
  const tx = db.transaction(META_STORE, 'readonly')
  const request = tx.objectStore(META_STORE).get('schemaVersion')
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      db.close()
      resolve((request.result as number | undefined) ?? 0)
    }
    request.onerror = () => {
      db.close()
      reject(request.error)
    }
  })
}

/** Persist the schema version. */
export async function setSchemaVersion(v: number): Promise<void> {
  const db = await openVectorDB()
  const tx = db.transaction(META_STORE, 'readwrite')
  tx.objectStore(META_STORE).put(v, 'schemaVersion')
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => {
      db.close()
      resolve()
    }
    tx.onerror = () => {
      db.close()
      reject(tx.error)
    }
  })
}

// --- CRUD --------------------------------------------------------------------

export async function storeEmbeddings(
  records: { entryId: string; embedding: number[]; contentHash: string }[],
): Promise<void> {
  const db = await openVectorDB()
  const tx = db.transaction(STORE_NAME, 'readwrite')
  const store = tx.objectStore(STORE_NAME)
  const now = Date.now()
  for (const record of records) {
    store.put({
      entryId: record.entryId,
      embedding: record.embedding,
      contentHash: record.contentHash,
      updatedAt: now,
    } satisfies EmbeddingRecord)
  }
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => {
      db.close()
      resolve()
    }
    tx.onerror = () => {
      db.close()
      reject(tx.error)
    }
  })
}

export async function getEmbedding(
  entryId: string,
): Promise<EmbeddingRecord | undefined> {
  const db = await openVectorDB()
  const tx = db.transaction(STORE_NAME, 'readonly')
  const request = tx.objectStore(STORE_NAME).get(entryId)
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      db.close()
      resolve((request.result as EmbeddingRecord | undefined) ?? undefined)
    }
    request.onerror = () => {
      db.close()
      reject(request.error)
    }
  })
}

export async function getAllEmbeddings(): Promise<EmbeddingRecord[]> {
  const db = await openVectorDB()
  const tx = db.transaction(STORE_NAME, 'readonly')
  const request = tx.objectStore(STORE_NAME).getAll()
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      db.close()
      resolve(request.result as EmbeddingRecord[])
    }
    request.onerror = () => {
      db.close()
      reject(request.error)
    }
  })
}

export async function deleteEmbedding(entryId: string): Promise<void> {
  const db = await openVectorDB()
  const tx = db.transaction(STORE_NAME, 'readwrite')
  tx.objectStore(STORE_NAME).delete(entryId)
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => {
      db.close()
      resolve()
    }
    tx.onerror = () => {
      db.close()
      reject(tx.error)
    }
  })
}

export async function clearAll(): Promise<void> {
  const db = await openVectorDB()
  const tx = db.transaction(STORE_NAME, 'readwrite')
  tx.objectStore(STORE_NAME).clear()
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => {
      db.close()
      resolve()
    }
    tx.onerror = () => {
      db.close()
      reject(tx.error)
    }
  })
}

// --- Pruning -----------------------------------------------------------------

/** Delete embeddings whose entryId is NOT in activeEntryIds. Returns deleted count. */
export async function pruneStaleEmbeddings(
  activeEntryIds: Set<string>,
): Promise<number> {
  const db = await openVectorDB()
  const tx = db.transaction(STORE_NAME, 'readwrite')
  const request = tx.objectStore(STORE_NAME).openCursor()
  let deleted = 0
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      const cursor = request.result
      if (cursor) {
        const record = cursor.value as EmbeddingRecord
        if (!activeEntryIds.has(record.entryId)) {
          cursor.delete()
          deleted++
        }
        cursor.continue()
      }
    }
    tx.oncomplete = () => {
      db.close()
      resolve(deleted)
    }
    tx.onerror = () => {
      db.close()
      reject(tx.error)
    }
  })
}
